import {
  formatCompanyTypes,
  tradeLicenseIssuingAuthorities,
} from "@pages/vendor-license-request/constants";
import type {
  LicenseDocumentResponse,
  LicenseReceiptResponse,
  LicenseResponse,
} from "@services/licensing/types";
import type { TFunction } from "i18next";

export type LicenseStatus =
  | "ACCEPTED"
  | "APPROVED"
  | "PENDING"
  | "REJECTED"
  | "AWAITING_MANAGING_DIRECTOR_VERIFICATION"
  | "AWAITING_MANAGING_DIRECTOR_APPROVAL"
  | "AWAITING_UPLOAD_RECEIPT"
  | "REJECTED_MANAGING_DIRECTOR_VERIFICATION"
  | "REJECTED_BY_MANAGING_DIRECTOR"
  | string;

const LANG_BASE = "licensing";
type LicenseDetailsDTO = {
  status?: string;
  companyId?: string;
  companyName?: string;
  companyNameArabic?: string;
  companyType?: string;
  natureOfBusiness?: string;
  companyAddress?: string;
  port?: string;
  applicantEmail?: string;
  companyEmail?: string;
  telephone?: string;
  faxNumber?: string;
  mobileNumber?: string;
  employees?: number;
  vehicles?: number;
  purpose?: string;
  refOrderNo?: number;
  applicantName?: string;
  applicantPosition?: string;
  tradeLicenseNumber?: string;
  tradeLicenseValidityDate?: string;
  tradeLicenseIssuingAuthority?: string;
  documents?: LicenseDocumentResponse[];
  receipt?: LicenseReceiptResponse | null;
  fujairahRegistered?: boolean;
};

const getTradeLicenseIssuingAuthorityLabel = (value?: string): string | undefined =>
  tradeLicenseIssuingAuthorities.find((authority) => authority.value === value)?.label ?? value;

export function formatDate(dateString?: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function buildVendorLicenseDetails(input?: LicenseResponse): LicenseDetailsDTO {
  return {
    status: input?.status,
    companyId: input?.companyId ?? input?.taskDecision?.companyId,
    companyName: input?.companyName,
    companyNameArabic: input?.companyNameArabic,
    companyType: formatCompanyTypes(input?.companyTypes),
    natureOfBusiness: input?.natureOfBusinessInPof,
    companyAddress: input?.companyAddress,
    port: "Port of Fujairah",
    applicantEmail: input?.applicantEmail,
    companyEmail: input?.companyEmail,
    telephone: input?.telephoneNumber,
    faxNumber: input?.faxNumber,
    mobileNumber: input?.mobileNumber,
    employees: input?.numberOfEmployees,
    vehicles: input?.numberOfVehicles,
    purpose: input?.purposeOfRequest,
    refOrderNo: input?.refOrderNumber,
    applicantName: input?.applicantName,
    applicantPosition: input?.applicantPosition,
    tradeLicenseNumber: input?.tradingLicenseNumber ?? undefined,
    tradeLicenseValidityDate: input?.tradingLicenseValidityDate,
    tradeLicenseIssuingAuthority: getTradeLicenseIssuingAuthorityLabel(
      input?.tradeLicenseIssuingAuthority ?? undefined,
    ),
    documents: input?.documents,
    receipt: input?.receipt,
    fujairahRegistered: input?.fujairahRegistered,
  };
}

export function getCompanyDetailsItems({
  details,
  TEST_ID_BASE,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
  t,
}: {
  details?: LicenseDetailsDTO | null;
  TEST_ID_BASE: string;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
  t: TFunction;
}) {
  const items = [
    {
      testId: `${TEST_ID_BASE}-company-name`,
      translate: t(`${LANG_BASE}.companyName`),
      text: details?.companyName || "-",
      wrapperStyle: wrapperFirstStyleClass,
    },
    ...(details?.companyId
      ? [
          {
            testId: `${TEST_ID_BASE}-company-id`,
            translate: t(`${LANG_BASE}.company-id`),
            text: details.companyId,
            wrapperStyle: wrapperStyleClass,
          },
        ]
      : []),
    {
      testId: `${TEST_ID_BASE}-company-name-arabic`,
      translate: t(`${LANG_BASE}.company-name-arabic`),
      text: details?.companyNameArabic || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-company-type`,
      translate: t(`${LANG_BASE}.companyType`),
      text: details?.companyType || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-nature-of-business`,
      translate: t(`${LANG_BASE}.natureOfBusiness`),
      text: details?.natureOfBusiness || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-company-address`,
      translate: t(`${LANG_BASE}.companyAddress`),
      text: details?.companyAddress || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-port`,
      translate: t(`${LANG_BASE}.port`),
      text: details?.port || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-company-email`,
      translate: t(`${LANG_BASE}.email`),
      text: details?.companyEmail || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-telephone`,
      translate: t(`${LANG_BASE}.telephone`),
      text: details?.telephone || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-fax-number`,
      translate: t(`${LANG_BASE}.faxNumber`),
      text: details?.faxNumber || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-mobile-number`,
      translate: t(`${LANG_BASE}.mobileNumber`),
      text: details?.mobileNumber || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-employees`,
      translate: t(`${LANG_BASE}.employees`),
      text:
        details?.employees !== undefined && details?.employees !== null && details?.employees !== 0
          ? String(details.employees)
          : "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-vehicles`,
      translate: t(`${LANG_BASE}.vehicles`),
      text: details?.vehicles || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-purpose`,
      translate: t(`${LANG_BASE}.purpose`),
      text: details?.purpose || "-",
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-ref-order-no`,
      translate: t(`${LANG_BASE}.refOrderNo`),
      text: details?.refOrderNo || "-",
      wrapperStyle: wrapperStyleClass,
    },
  ];

  return items.map((item) => ({
    ...item,
    textStyles: "font-normal",
    tranStyle: transStyleClass,
  }));
}

export function getApplicantDetailsItems({
  details,
  TEST_ID_BASE,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
  t,
}: {
  details?: LicenseDetailsDTO | null;
  TEST_ID_BASE: string;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
  t: TFunction;
}) {
  return [
    {
      testId: `${TEST_ID_BASE}-applicant-name`,
      translate: t(`${LANG_BASE}.applicantName`),
      text: details?.applicantName || "-",
      textStyles: "font-normal",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperFirstStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-applicant-position`,
      translate: t(`${LANG_BASE}.applicantPosition`),
      text: details?.applicantPosition || "-",
      textStyles: "font-normal",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-applicant-email`,
      translate: t(`${LANG_BASE}.email`),
      text: details?.applicantEmail || "-",
      textStyles: "font-normal",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    },
  ];
}

export function getTradeLicenseItems({
  details,
  TEST_ID_BASE,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
  t,
}: {
  details?: LicenseDetailsDTO | null;
  TEST_ID_BASE: string;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
  t: TFunction;
}) {
  const items = [
    {
      testId: `${TEST_ID_BASE}-trade-license-number`,
      translate: t(`${LANG_BASE}.tradeLicenseNumber`),
      text: details?.tradeLicenseNumber || "-",
      textStyles: "font-normal",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperFirstStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-trade-license-entry-date`,
      translate: t(`${LANG_BASE}.tradeLicenseValidityDate`),
      text: details?.tradeLicenseValidityDate ? formatDate(details.tradeLicenseValidityDate) : "-",
      textStyles: "font-normal",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-trade-license-issuing-authority`,
      translate: t(`${LANG_BASE}.tradeLicenseIssuingAuthority`),
      text: details?.tradeLicenseIssuingAuthority || "-",
      textStyles: "font-normal",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    },
  ] as any[];

  if (details?.fujairahRegistered !== undefined) {
    items.push({
      testId: `${TEST_ID_BASE}-fujairah-registered`,
      translate: t(`${LANG_BASE}.fujairah-registered-company`),
      text: details.fujairahRegistered ? t("common.yes") : t("common.no"),
      textStyles: "font-normal",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
  }

  return items;
}

export function getAdditionalInformationItems({
  details,
  TEST_ID_BASE,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
  t,
}: {
  details?: LicenseDetailsDTO | null;
  TEST_ID_BASE: string;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
  t: TFunction;
}) {
  // if it is a port operator license
  if (details?.receipt) {
    return [
      {
        testId: `${TEST_ID_BASE}-receipt-information-number`,
        translate: t(`${LANG_BASE}.receipt-number`),
        text: details?.receipt.receiptNumber || "-",
        textStyles: "font-normal",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperFirstStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-receipt-information-date`,
        translate: t(`${LANG_BASE}.receipt-date`),
        text: details?.receipt.receiptDate ? formatDate(details.receipt.receiptDate) : "-",
        textStyles: "font-normal",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-receipt-information-amount`,
        translate: t(`${LANG_BASE}.receipt-amount`),
        text: details?.receipt.receiptAmount || "-",
        textStyles: "font-normal",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
    ];
  }
  return [];
}

export function getLicenseStatusFormat(
  status: LicenseStatus | undefined,
  t: TFunction,
): {
  badgeType: "success" | "warning" | "error";
  label: string;
} {
  if (!status) {
    return { badgeType: "warning", label: "-" };
  }

  const map: Record<
    string,
    { badgeType: "success" | "warning" | "error"; key: string; defaultLabel: string }
  > = {
    APPROVED: {
      badgeType: "success",
      key: "licensing.status-labels.APPROVED",
      defaultLabel: "Approved",
    },
    PENDING: {
      badgeType: "warning",
      key: "licensing.status-labels.PENDING",
      defaultLabel: "Pending Approval",
    },
    REJECTED: {
      badgeType: "error",
      key: "licensing.status-labels.REJECTED",
      defaultLabel: "Rejected",
    },
    REJECTED_MANAGING_DIRECTOR_VERIFICATION: {
      badgeType: "error",
      key: "licensing.status-labels.REJECTED_MANAGING_DIRECTOR_VERIFICATION",
      defaultLabel: "Rejected by Managing Director Verification",
    },
    EXPIRED: {
      badgeType: "error",
      key: "licensing.status-labels.EXPIRED",
      defaultLabel: "Expired",
    },
    PENDING_RENEWAL: {
      badgeType: "success",
      key: "licensing.status-labels.PENDING_RENEWAL",
      defaultLabel: "Pending Renewal",
    },
    RENEWED: {
      badgeType: "success",
      key: "licensing.status-labels.RENEWED",
      defaultLabel: "Renewed",
    },
    AWAITING_MANAGING_DIRECTOR_VERIFICATION: {
      badgeType: "warning",
      key: "licensing.status-labels.AWAITING_MANAGING_DIRECTOR_VERIFICATION",
      defaultLabel: "Awaiting Managing Director Verification",
    },
    AWAITING_MANAGING_DIRECTOR_APPROVAL: {
      badgeType: "warning",
      key: "licensing.status-labels.AWAITING_MANAGING_DIRECTOR_APPROVAL",
      defaultLabel: "Awaiting Managing Director Approval",
    },
    AWAITING_UPLOAD_RECEIPT: {
      badgeType: "warning",
      key: "licensing.status-labels.AWAITING_UPLOAD_RECEIPT",
      defaultLabel: "Awaiting Receipt Upload",
    },
    REJECTED_BY_MANAGING_DIRECTOR: {
      badgeType: "error",
      key: "licensing.status-labels.REJECTED_BY_MANAGING_DIRECTOR",
      defaultLabel: "Rejected by Managing Director Office",
    },
  };

  const mapped = map[status];
  if (mapped) {
    return { badgeType: mapped.badgeType, label: t(mapped.key, mapped.defaultLabel) };
  }

  const fallbackLabel = status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

  return {
    badgeType: "warning",
    label: t(`licensing.status-labels.${status}`, fallbackLabel),
  };
}

export function getFileExtensionFromUrl(url?: string | null): string {
  if (!url) return "";
  const cleanUrl = url.split("?")[0].split("#")[0];
  const lastDotIndex = cleanUrl.lastIndexOf(".");
  if (lastDotIndex === -1) return "";
  return cleanUrl.substring(lastDotIndex + 1);
}

const LICENSE_DOCUMENT_DISPLAY_ORDER: Record<string, number> = {
  TRADE_LICENSE: 0,
  COMPANY_INCORPORATION: 1,
};

export function sortLicenseDocumentsForDisplay(documents: LicenseDocumentResponse[] = []) {
  return [...documents].sort((left, right) => {
    const leftOrder = LICENSE_DOCUMENT_DISPLAY_ORDER[left.documentType] ?? Number.MAX_SAFE_INTEGER;
    const rightOrder =
      LICENSE_DOCUMENT_DISPLAY_ORDER[right.documentType] ?? Number.MAX_SAFE_INTEGER;

    return leftOrder - rightOrder;
  });
}

export function getDocumentTitle(documentType: string, t: TFunction): string {
  if (documentType === "TRADE_LICENSE") {
    return t("licensing.tradeLicense");
  }
  if (documentType === "COMPANY_INCORPORATION") {
    return t("licensing.companyIncorporation");
  }
  if (documentType === "PORT_OPERATOR_LICENSE") {
    return t("licensing.port-operator-license-title");
  }
  if (documentType === "OTHER_DOCUMENT") {
    return t("licensing.other-documents-title");
  }

  return documentType
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}
